import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { SearchInput } from '../components/search/SearchInput';
import { SearchSuggestions } from '../components/search/SearchSuggestions';
import { SearchResults } from '../components/search/SearchResults';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';

const Search: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [actualSearchQuery, setActualSearchQuery] = useState(''); // Valor original do banco para busca
  const [isSearching, setIsSearching] = useState(false);
  const [lastSearchedTerm, setLastSearchedTerm] = useState<string>('');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { t, currentLanguage } = useI18n();

  // Mapeamento reverso: valor traduzido -> valor original do banco
  const getOriginalValueFromTranslation = useMemo(() => {
    // Mapeamento de interesses: traduzido -> original
    const interestReverseMapping: Record<string, string> = {
      // Português -> Inglês
      'Leitura': 'Reading',
      'Caminhada / Trekking': 'Hiking / Trekking',
      'Fotografia': 'Photography',
      'Jogos (Videogames / Jogos de Tabuleiro)': 'Gaming (Video Games / Board Games)',
      'Culinária / Confeitaria': 'Cooking / Baking',
      'Viagens': 'Traveling',
      'Ciclismo (Estrada / Montanha)': 'Cycling (Road / Mountain)',
      'Corrida': 'Running',
      'Musculação / Fitness': 'Weightlifting / Fitness',
      'Yoga / Pilates': 'Yoga / Pilates',
      'Natação': 'Swimming',
      'Surfe / Stand Up Paddle': 'Surfing / Paddleboarding',
      'Pesca': 'Fishing',
      'Jardinagem': 'Gardening',
      'Música (Tocar instrumento / Ouvir)': 'Music (Playing an instrument / Listening)',
      'Podcasts': 'Podcasts',
      'Filmes / Séries de TV': 'Movies / TV Shows',
      'Desenho / Pintura': 'Drawing / Painting',
      'Escrita': 'Writing',
      'Aprender Idiomas': 'Learning Languages',
      'Voluntariado': 'Volunteering',
      'Meditação': 'Meditation',
      'Acampamento': 'Camping',
      'Motociclismo': 'Motorcycling',
      'Carros / Esportes Motorizados': 'Cars / Motorsports',
      'Colecionismo (ex: cartas, moedas, selos)': 'Collecting (e.g., cards, coins, stamps)',
      'Faça Você Mesmo / Reformas': 'DIY / Home Improvement',
      'Xadrez / Jogos de Estratégia': 'Chess / Strategy Games',
      'Artes Marciais (além do Jiu-Jitsu)': 'Martial Arts (other than Jiu-Jitsu)',
      'Esportes em Equipe (ex: Futebol, Basquete, Vôlei)': 'Team Sports (e.g., Soccer, Basketball, Volleyball)',
      'Golfe': 'Golf',
      'Tênis': 'Tennis',
      'Tênis de Mesa / Ping Pong': 'Table Tennis / Ping Pong',
      'Esqui / Snowboard': 'Skiing / Snowboarding',
      'Boliche': 'Bowling',
      'Dança': 'Dancing',
      'Moda / Estilo': 'Fashion / Style',
      'Astrologia / Espiritualidade': 'Astrology / Spirituality',
      'Podcasting / Criação de Conteúdo': 'Podcasting / Content Creation',
      'Marcenaria': 'Woodworking',
      'Tricô / Crochê': 'Knitting / Crocheting',
      'Caligrafia': 'Calligraphy',
      'Programação / Código (como hobby)': 'Coding / Programming (as a hobby)',
      'Cervejaria / Café': 'Brewing (Beer / Coffee)',
      'Astronomia': 'Astronomy',
      'Observação de Aves': 'Birdwatching',
      'Tiro com Arco': 'Archery',
      'Caça': 'Hunting',
      'Quebra-Cabeças': 'Puzzles',
      'Outro': 'Other',
    };

    // Mapeamento de profissões: traduzido -> original
    const professionReverseMapping: Record<string, string> = {
      // Português -> Inglês
      'Contador': 'Accountant',
      'Acupunturista': 'Acupuncturist',
      'Administrador': 'Administrator',
      'Animador': 'Animator',
      'Artista': 'Artist',
      'Barbeiro / Cabeleireiro': 'Barber / Hair Stylist',
      'Analista de Negócios': 'Business Analyst',
      'Carpinteiro': 'Carpenter',
      'Chef / Cozinheiro': 'Chef / Cook',
      'Quiropraxista': 'Chiropractor',
      'Engenheiro de Cloud': 'Cloud Engineer',
      'Treinador': 'Coach',
      'Consultor': 'Consultant',
      'Criador de Conteúdo': 'Content Creator',
      'Representante de Atendimento ao Cliente': 'Customer Service Representative',
      'Analista de Cibersegurança': 'Cybersecurity Analyst',
      'Cientista de Dados': 'Data Scientist',
      'Dentista': 'Dentist',
      'Engenheiro DevOps': 'DevOps Engineer',
      'Especialista em Marketing Digital': 'Digital Marketing Specialist',
      'Médico': 'Doctor',
      'Eletricista': 'Electrician',
      'Empreendedor / Proprietário de Negócio': 'Entrepreneur / Business Owner',
      'Consultor Financeiro': 'Financial Advisor',
      'Analista Financeiro': 'Financial Analyst',
      'Bombeiro': 'Firefighter',
      'Designer Gráfico': 'Graphic Designer',
      'Gerente de Recursos Humanos': 'Human Resources Manager',
      'Banqueiro de Investimento': 'Investment Banker',
      'Consultor de TI': 'IT Consultant',
      'Especialista em Suporte de TI': 'IT Support Specialist',
      'Proprietário de Academia de Jiu-Jitsu': 'Jiu-Jitsu Academy Owner',
      'Instrutor / Professor de Jiu-Jitsu': 'Jiu-Jitsu Instructor / Professor',
      'Jornalista': 'Journalist',
      'Juiz': 'Judge',
      'Agente de Segurança Pública': 'Law Enforcement Officer',
      'Advogado': 'Lawyer / Attorney',
      'Bibliotecário': 'Librarian',
      'Coordenador de Logística': 'Logistics Coordinator',
      'Especialista em Marketing': 'Marketing Specialist',
      'Massoterapeuta': 'Massage Therapist',
      'Mecânico': 'Mechanic',
      'Assistente Médico': 'Medical Assistant',
      'Militar': 'Military Personnel',
      'Desenvolvedor de Aplicativos Móveis': 'Mobile App Developer',
      'Músico': 'Musician',
      'Administrador de Rede': 'Network Administrator',
      'Enfermeiro': 'Nurse',
      'Nutricionista': 'Nutritionist / Dietitian',
      'Gerente de Operações': 'Operations Manager',
      'Outro': 'Other',
      'Assistente Jurídico': 'Paralegal',
      'Personal Trainer': 'Personal Trainer',
      'Farmacêutico': 'Pharmacist',
      'Fotógrafo': 'Photographer',
      'Fisioterapeuta': 'Physical Therapist',
      'Encanador': 'Plumber',
      'Gerente de Produto': 'Product Manager',
      'Atleta Profissional de Jiu-Jitsu': 'Professional Jiu-Jitsu Athlete',
      'Professor': 'Professor',
      'Gerente de Projetos': 'Project Manager',
      'Especialista em Relações Públicas': 'Public Relations Specialist',
      'Corretor de Imóveis': 'Real Estate Agent',
      'Pesquisador': 'Researcher',
      'Gerente de Restaurante': 'Restaurant Manager',
      'Vendedor': 'Retail Associate',
      'Aposentado': 'Retired',
      'Profissional de Vendas': 'Sales Professional',
      'Gerente de Mídias Sociais': 'Social Media Manager',
      'Assistente Social': 'Social Worker',
      'Desenvolvedor de Software': 'Software Developer',
      'Psicólogo do Esporte': 'Sports Psychologist',
      'Estudante': 'Student',
      'Analista de Sistemas': 'Systems Analyst',
      'Professor / Educador': 'Teacher / Educator',
      'Desempregado / Procurando Oportunidades': 'Unemployed / Seeking Opportunities',
      'Designer UX/UI': 'UX/UI Designer',
      'Videógrafo': 'Videographer',
      'Desenvolvedor Web': 'Web Developer',
      'Escritor / Editor': 'Writer / Editor',
    };

    return (translatedValue: string): string => {
      // Primeiro tenta encontrar em interesses
      if (interestReverseMapping[translatedValue]) {
        return interestReverseMapping[translatedValue];
      }
      // Depois tenta encontrar em profissões
      if (professionReverseMapping[translatedValue]) {
        return professionReverseMapping[translatedValue];
      }
      // Se não encontrar, retorna o valor original (pode ser que o usuário digitou algo manualmente)
      return translatedValue;
    };
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when search results are displayed
  useEffect(() => {
    if (isSearching) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isSearching]);

  // Função para salvar termo de busca
  const saveSearchTerm = async (term: string, userId: string) => {
    try {
      const token = localStorage.getItem('supabase.auth.token');
      const parsedToken = token ? JSON.parse(token).access_token : null;
      
      const { getEdgeFunctionUrl } = await import('@/utils/url');
      const functionUrl = getEdgeFunctionUrl('save_search_term');
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${parsedToken}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify({ term, userId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    } catch (error) {
      console.error('Save search term error:', error);
      throw error;
    }
  };

  // Restaurar estado da busca quando retornar da página de detalhe
  useEffect(() => {
    if (location.state?.searchState && location.state?.preserveSearch) {
      setSearchQuery(location.state.searchState.query || '');
      setIsSearching(location.state.searchState.query ? true : false);
      setLastSearchedTerm(location.state.searchState.query || '');
    }
  }, [location.state]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // Converter valor traduzido para original do banco
    const originalValue = getOriginalValueFromTranslation(query);
    setActualSearchQuery(originalValue);
    setIsSearching(query.trim().length > 0);
  };

  const handleSearchSubmit = async (query: string) => {
    setSearchQuery(query);
    // Converter valor traduzido para original do banco antes de buscar
    const originalValue = getOriginalValueFromTranslation(query);
    setActualSearchQuery(originalValue);
    setIsSearching(true);
    
    // Salvar o termo de busca apenas quando o usuário pressionar Enter
    // Salvar o valor original do banco para manter consistência
    if (user?.id && originalValue.trim()) {
      try {
        await saveSearchTerm(originalValue.trim(), user.id);
        
        // Atualizar o último termo buscado para atualizar a lista localmente
        setLastSearchedTerm(originalValue.trim());
      } catch (error) {
        console.error('❌ Failed to save search term on Enter:', error);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setLastSearchedTerm(''); // Limpar o último termo buscado
  };

  // Teste: se não há usuário logado, usar um ID fixo para teste
  const testUserId = user?.id || 'b85f7965-8e9f-449b-9718-08a5b2fa0f3b'; // ID da Ana para teste

  return (
    <div className="min-h-screen w-screen pb-20" style={{ background: '#160C0C' }}>
      <TopNavigation variant="subtle" />
      
      <main className="content-with-nav px-5">
        <div className="py-6">
          <SearchInput
            autoFocus
            value={searchQuery}
            onChange={handleSearch}
            onClear={handleClearSearch}
            onSearch={handleSearchSubmit}
            placeholder={t('search.find_users_and_inspiration')}
          />
          
          {!isSearching ? (
            <SearchSuggestions onSearch={handleSearch} newSearchTerm={lastSearchedTerm} />
          ) : (
            <SearchResults 
              query={actualSearchQuery || searchQuery} 
              userId={testUserId}
            />
          )}
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default Search; 